import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useCallback, useEffect, useRef, useState } from "react";
import { GtVisitsListResponseDto, GtVisitsRequestDto, Option } from "./Dashboard.Type";
import dayjs from "dayjs";
import DashboardService from "./Dashboard.Service";
import Toast from "../../Utility/Toast";
import { MultiSelect } from "react-multi-select-component";
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import { MAX_SELECTABLE_IMAGES } from "../../Utility/constants";
import { usePhotoDialogueStyles, photoDialoguePaperSx, photoDialogueBackdropSx } from "./Dashboard.Style";
import DateRangePickerPopover, { DateRangeValue } from "./DateRangePickerPopover";

interface GTPhotoDialogueProps {
    open: boolean;
    onClose: () => void;
    onDownload: (visitIds: string[]) => Promise<void>;
}

const initialRequest: GtVisitsRequestDto = {
    search: "",
    fromDate: null,
    toDate: null,
};

const customMessages = {
    allItemsAreSelected: "All",
    selectSomeItems: "Select",
    selectAllFiltered: "All",
    noOptions: "No Visits Found",
};

const GTPhotoDialogue = ({ open, onClose, onDownload }: GTPhotoDialogueProps) => {
    const classes = usePhotoDialogueStyles();

    const [request, setRequest] = useState<GtVisitsRequestDto>(initialRequest);
    const [loading, setLoading] = useState(false);
    const [visitOptions, setVisitOptions] = useState<Option[]>([]);
    const [visitImageCounts, setVisitImageCounts] = useState<Record<string, number>>({});
    const [selectedVisits, setSelectedVisits] = useState<Option[]>([]);
    const [maxDownloadLimit, setMaxDownloadLimit] = useState(MAX_SELECTABLE_IMAGES);

    const [calendarAnchorEl, setCalendarAnchorEl] = useState<HTMLElement | null>(null);
    const dateRangeRef = useRef<HTMLDivElement>(null);

    const requestIdRef = useRef(0);

    const calendarOpen = Boolean(calendarAnchorEl);

    const totalImages = selectedVisits.reduce((sum, sel) => {
        return sum + (visitImageCounts[sel.value] ?? 0);
    }, 0);
    const limitExceeded = totalImages > maxDownloadLimit;

    useEffect(() => {
        if (!open) {
            requestIdRef.current += 1;
            setRequest(initialRequest);
            setVisitOptions([]);
            setVisitImageCounts({});
            setSelectedVisits([]);
            setCalendarAnchorEl(null);
        }
    }, [open]);

    const loadVisits = useCallback(async (updatedRequest: GtVisitsRequestDto) => {
        if (!updatedRequest.fromDate || !updatedRequest.toDate) return;
        const requestId = ++requestIdRef.current;
        try {
            setLoading(true);
            const response: GtVisitsListResponseDto = await DashboardService.getVisitByDate(updatedRequest);

            if (requestId !== requestIdRef.current) return;
            const options = response?.gtData?.map((visit) => ({
                label: visit?.visitId,
                value: visit?.visitId,
            })) ?? [];
            setMaxDownloadLimit(
                response?.maxPhotoDownload ?? MAX_SELECTABLE_IMAGES
            );
            const imageCounts: Record<string, number> = {};
            response?.gtData?.forEach((visit) => {
                imageCounts[visit?.visitId] = visit?.imageCount ?? 0;
            });
            setVisitOptions(options);
            setVisitImageCounts(imageCounts);
            setSelectedVisits([]);
        } catch (error: any) {
            if (requestId !== requestIdRef.current) return;
            Toast.error("Failed to load visits");
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, []);

    const openCalendar = () => {
        setCalendarAnchorEl(dateRangeRef.current);
    };

    const handleCalendarCancel = () => {
        setCalendarAnchorEl(null);
    };

    const handleCalendarApply = (range: DateRangeValue) => {
        const updatedRequest = { ...request, fromDate: range.fromDate, toDate: range.toDate };
        setRequest(updatedRequest);
        setCalendarAnchorEl(null);
        if (updatedRequest.fromDate && updatedRequest.toDate) loadVisits(updatedRequest);
    };

    const handleDownload = () => {
        const visitIds = selectedVisits.map(v => v.value);
        onClose();
        onDownload(visitIds);
    };

    const customDisplayValue = (selected: Option[]) => {
        if (selected.length === 0) return "Select Visit ID";
        if (selected.length === visitOptions.length) return "All";
        return `${selected.length} selected`;
    };

    const datesSelected = !!(request.fromDate && request.toDate);
    const canDownload = datesSelected && selectedVisits.length > 0 && !limitExceeded;

    const formattedDateRange = (request.fromDate || request.toDate)
        ? `${request.fromDate ? dayjs(request.fromDate).format("DD/MM/YYYY") : '...'} - ${request.toDate ? dayjs(request.toDate).format("DD/MM/YYYY") : '...'}`
        : '';

    return (
        <Dialog
            open={open}
            onClose={(_event, reason) => {
                if (reason === 'backdropClick') return;
                onClose();
            }}
            maxWidth={false}
            PaperProps={{ sx: photoDialoguePaperSx }}
            BackdropProps={{ sx: photoDialogueBackdropSx }}
        >
            <div className={classes.dialogBody}>

                <h2 className={classes.title}>
                    GT Photo Download
                </h2>

                {/* Date Range */}
                <div className={classes.dateRangeSection}>
                    <label className={classes.sectionLabel}>
                        Date Range
                    </label>
                    <div ref={dateRangeRef}>
                        <FormControl fullWidth>
                            <TextField
                                size="small"
                                placeholder="Start Date - End Date"
                                value={formattedDateRange}
                                onClick={openCalendar}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" edge="end" onClick={openCalendar}>
                                                <CalendarTodayIcon fontSize="small" sx={{ color: 'rgba(0,0,0,0.54)' }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    width: '100%',
                                    '& .MuiInputBase-root': { height: 34, backgroundColor: '#fff', cursor: 'pointer' },
                                    '& .MuiInputBase-input': { padding: '6px 8px', fontSize: 12, cursor: 'pointer' },
                                }}
                            />
                        </FormControl>
                    </div>
                </div>

                <DateRangePickerPopover
                    open={calendarOpen}
                    anchorEl={calendarAnchorEl}
                    value={{ fromDate: request.fromDate ?? null, toDate: request.toDate ?? null }}
                    maxDate={dayjs().toDate()}
                    onApply={handleCalendarApply}
                    onCancel={handleCalendarCancel}
                />

                {datesSelected && (
                    <div className={classes.visitIdSection}>
                        <div className={classes.visitIdHeaderRow}>
                            <label className={classes.inlineLabel}>Visit ID</label>

                            <span className={
                                limitExceeded
                                    ? classes.visitBadgeError
                                    : totalImages > 0
                                        ? classes.visitBadgeSelected
                                        : classes.visitBadge
                            }>
                                {totalImages > 0
                                    ? `${totalImages} image${totalImages > 1 ? "s" : ""} selected`
                                    : "No images selected"}
                            </span>
                        </div>

                        {loading ? (
                            <div className={classes.loadingText}>Loading visits…</div>
                        ) : (
                            <div className={classes.gtPhotoMultiselect}>
                                <MultiSelect
                                    options={visitOptions}
                                    value={selectedVisits}
                                    onChange={setSelectedVisits}
                                    hasSelectAll={true}
                                    labelledBy="Visit ID"
                                    valueRenderer={customDisplayValue}
                                    overrideStrings={customMessages}
                                    shouldToggleOnHover={false}
                                    defaultIsOpen={true}
                                />
                            </div>
                        )}
                    </div>
                )}

                <div className={classes.spacer} />
            </div>

            <div className={classes.footer}>
                <button
                    onClick={onClose}
                    className={`${classes.actionButtonBase} ${classes.cancelButton}`}
                >
                    Cancel
                </button>
                <button
                    onMouseDown={(e) => {
                        e.preventDefault();
                        if (canDownload) handleDownload();
                    }}
                    disabled={!canDownload}
                    className={`${classes.actionButtonBase} ${classes.downloadButtonBase} ${canDownload ? classes.downloadButtonEnabled : classes.downloadButtonDisabled}`}
                >
                    Download
                </button>
            </div>
        </Dialog>
    );
};

export default GTPhotoDialogue;
